import { InfoTooltip } from '../InfoTooltip.js';
import { METRIC_EXPLANATIONS } from '../../data/metric-explanations.js';
import { formatScoreWithLabel } from '../../lib/scoreFormatter.js';

export interface WalletIntelData {
  riskScore: number;
  riskLevel: string;
  reasons: string[];
  deployer: string | null;
  fundingSource: string | null;
  // Provider-level fields
  creatorAddress?: string | null;
  ownerAddress?: string | null;
  ownerCanChangeBalance?: boolean | null;
  hiddenOwner?: boolean | null;
  dataUnavailableReason?: string;
}

const NO_DATA = 'No data available yet';

function riskColor(level: string): string {
  if (level === 'HIGH') return '#e74c3c';
  if (level === 'MEDIUM') return '#f39c12';
  return '#27ae60';
}

function truncAddr(addr: string): string {
  if (addr.length <= 14) return addr;
  return `${addr.slice(0, 8)}...${addr.slice(-6)}`;
}

export function WalletIntelPanel({ data }: { data: WalletIntelData | null | undefined }) {
  const d = data ?? null;

  if (!d) return null;

  if (d.dataUnavailableReason) {
    return (
      <div className="dash-card ws-forensic-section">
        <h3 className="ws-forensic-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          Wallet Intelligence
          <InfoTooltip explain={METRIC_EXPLANATIONS.walletRisk} compact />
        </h3>
        <div className="intel-metric-card card">
          <div className="intel-metric">
            <span className="intel-metric-label">Wallet Analysis</span>
            <span className="intel-metric-value" style={{ fontSize: '0.85rem' }}>
              {d.dataUnavailableReason}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dash-card ws-forensic-section">
      <h3 className="ws-forensic-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        Wallet Intelligence
        <InfoTooltip explain={METRIC_EXPLANATIONS.walletRisk} compact />
      </h3>
      <div className="intel-grid intel-grid-3">
        <div className="intel-metric-card card">
          <div className="intel-metric">
            <span className="intel-metric-label">
              Creator Risk Score
              <InfoTooltip explain={METRIC_EXPLANATIONS.walletRisk} compact />
            </span>
            <span className="intel-metric-value" style={{ color: riskColor(d.riskLevel) }}>
              {formatScoreWithLabel(d.riskScore)}
            </span>
          </div>
        </div>
        <div className="intel-metric-card card">
          <div className="intel-metric">
            <span className="intel-metric-label">Risk Assessment</span>
            <span className={`intel-bool-badge ${d.riskLevel === 'LOW' ? 'intel-bool-good' : 'intel-bool-bad'}`}>
              {d.riskLevel === 'LOW' ? 'Low Risk Indicators' : d.riskLevel === 'HIGH' ? 'Severe Risk Indicators' : 'Moderate Risk Indicators'}
            </span>
          </div>
        </div>
        <div className="intel-metric-card card">
          <div className="intel-metric">
            <span className="intel-metric-label">Token Deployer</span>
            <span className="intel-metric-value" style={{ fontSize: '0.75rem' }}>
              {d.deployer ? truncAddr(d.deployer) : NO_DATA}
            </span>
          </div>
        </div>
        <div className="intel-metric-card card">
          <div className="intel-metric">
            <span className="intel-metric-label">Funding Source</span>
            <span className="intel-metric-value" style={{ fontSize: '0.75rem' }}>
              {d.fundingSource ? truncAddr(d.fundingSource) : NO_DATA}
            </span>
          </div>
        </div>
        <div className="intel-metric-card card">
          <div className="intel-metric">
            <span className="intel-metric-label">Creator Address</span>
            <span className="intel-metric-value" style={{ fontSize: '0.75rem' }}>
              {d.creatorAddress ? truncAddr(d.creatorAddress) : NO_DATA}
            </span>
          </div>
        </div>
        <div className="intel-metric-card card">
          <div className="intel-metric">
            <span className="intel-metric-label">Owner Address</span>
            <span className="intel-metric-value" style={{ fontSize: '0.75rem' }}>
              {d.ownerAddress ? truncAddr(d.ownerAddress) : NO_DATA}
            </span>
          </div>
        </div>
        <div className="intel-metric-card card">
          <div className="intel-metric">
            <span className="intel-metric-label">
              Owner Control
              <InfoTooltip explain={METRIC_EXPLANATIONS.mintFunction} compact />
            </span>
            {d.ownerCanChangeBalance != null ? (
              <span className={`intel-bool-badge ${!d.ownerCanChangeBalance ? 'intel-bool-good' : 'intel-bool-bad'}`}>
                {d.ownerCanChangeBalance ? 'Yes' : 'No'}
              </span>
            ) : (
              <span className="intel-metric-value">{NO_DATA}</span>
            )}
          </div>
        </div>
        <div className="intel-metric-card card">
          <div className="intel-metric">
            <span className="intel-metric-label">Hidden Owner</span>
            {d.hiddenOwner != null ? (
              <span className={`intel-bool-badge ${!d.hiddenOwner ? 'intel-bool-good' : 'intel-bool-bad'}`}>
                {d.hiddenOwner ? 'Yes' : 'No'}
              </span>
            ) : (
              <span className="intel-metric-value">{NO_DATA}</span>
            )}
          </div>
        </div>
      </div>
      {d.reasons && d.reasons.length > 0 && (
        <ul style={{ margin: '0.5rem 0 0', padding: '0 0 0 1.2rem', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
          {d.reasons.map((r, i) => <li key={i}>{r}</li>)}
        </ul>
      )}
      {(!d.reasons || d.reasons.length === 0) && (
        <p style={{ margin: '0.5rem 0 0', fontSize: '0.8rem', color: 'var(--text-muted)' }}>No wallet risk signals detected</p>
      )}
    </div>
  );
}
